import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate, Link } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { Home, Trophy, Radio, Users, Menu, Activity } from "lucide-react";

// Pages - Public
import HomePage from "@/pages/HomePage";
import LeaguesPage from "@/pages/LeaguesPage";
import LeagueDetailPage from "@/pages/LeagueDetailPage";
import TeamDetailPage from "@/pages/TeamDetailPage";
import MatchDetailPage from "@/pages/MatchDetailPage";
import LiveMatchesPage from "@/pages/LiveMatchesPage";
import LiveMatchView from "@/pages/LiveMatchView";
import PlayerStatsPage from "@/pages/PlayerStatsPage";
import PlayerProfilePage from "@/pages/PlayerProfilePage";
import LessonsPage from "@/pages/LessonsPage";
import ScoreOverlay from "@/pages/ScoreOverlay";
import GuidePage from "@/pages/GuidePage";

// Pages - Auth
import LoginPage from "@/pages/auth/LoginPage";
import SignupPage from "@/pages/auth/SignupPage";
import VerifyEmailPage from "@/pages/auth/VerifyEmailPage";

// Pages - Admin
import AdminDashboard from "@/pages/admin/AdminDashboard";
import LeagueManager from "@/pages/admin/LeagueManager";
import TeamManager from "@/pages/admin/TeamManager";
import PlayerManager from "@/pages/admin/PlayerManager";
import MatchScheduler from "@/pages/admin/MatchScheduler";
import UserManager from "@/pages/admin/UserManager";
import ManageAdminsPage from "@/pages/admin/ManageAdminsPage";
import ManageUsersPage from "@/pages/admin/ManageUsersPage";
import BulkDataManager from "@/pages/admin/BulkDataManager";

// Pages - Scorer
import ScorerDashboard from "@/pages/scorer/ScorerDashboard";
import LiveScoring from "@/pages/scorer/LiveScoring";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

// Bottom Navigation Component
const BottomNav = ({ user }) => {
  const location = useLocation();
  
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const navItems = [
    { icon: Home, label: 'Home', path: '/' },
    { icon: Trophy, label: 'Leagues', path: '/leagues' },
    { icon: Radio, label: 'Live', path: '/live' },
    { icon: Activity, label: 'Stats', path: '/stats' },
    { icon: Menu, label: 'More', path: user ? (user.role === 'admin' ? '/admin' : user.role === 'scorer' ? '/scorer' : '/profile') : '/login' },
  ];

  return (
    <nav className="bottom-nav md:hidden">
      {navItems.map((item) => {
        const Icon = item.icon;
        return (
          <Link
            key={item.path}
            to={item.path}
            className={`bottom-nav-item ${isActive(item.path) ? 'active' : ''}`}
            data-testid={`nav-${item.label.toLowerCase()}`}
          >
            <Icon />
            <span>{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
};

// Protected Route
const ProtectedRoute = ({ children, requiredRole, user, loading }) => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { state: { from: location } });
    }
  }, [user, loading, navigate, location]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  if (requiredRole && user.role !== requiredRole && user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center p-8">
          <h1 className="text-2xl font-bold text-slate-900">Access Denied</h1>
          <p className="mt-2 text-slate-600">You don't have permission to access this page.</p>
          <Link to="/" className="mt-4 inline-block text-blue-600 hover:underline">Go Home</Link>
        </div>
      </div>
    );
  }

  return children;
};

// App Router
function AppRouter({ user, setUser, loading }) {
  const location = useLocation();
  const showBottomNav = !location.pathname.startsWith('/admin') && 
                        !location.pathname.startsWith('/scorer/match/') &&
                        !location.pathname.includes('/scoring');

  return (
    <>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage user={user} setUser={setUser} />} />
        <Route path="/leagues" element={<LeaguesPage user={user} setUser={setUser} />} />
        <Route path="/leagues/:leagueId" element={<LeagueDetailPage user={user} setUser={setUser} />} />
        <Route path="/teams" element={<LeaguesPage user={user} setUser={setUser} showTeams />} />
        <Route path="/teams/:teamId" element={<TeamDetailPage user={user} setUser={setUser} />} />
        <Route path="/matches/:matchId" element={<MatchDetailPage user={user} setUser={setUser} />} />
        <Route path="/live" element={<LiveMatchesPage user={user} setUser={setUser} />} />
        <Route path="/live/:matchId" element={<LiveMatchView />} />
        <Route path="/overlay/:matchId" element={<ScoreOverlay />} />
        <Route path="/stats" element={<PlayerStatsPage user={user} setUser={setUser} />} />
        <Route path="/players/:playerId" element={<PlayerProfilePage user={user} setUser={setUser} />} />
        <Route path="/lessons" element={<LessonsPage />} />
        <Route path="/guide" element={<GuidePage />} />

        {/* Auth Routes */}
        <Route path="/login" element={<LoginPage user={user} setUser={setUser} />} />
        <Route path="/signup" element={<SignupPage setUser={setUser} />} />
        <Route path="/verify-email" element={<VerifyEmailPage setUser={setUser} />} />

        {/* Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute requiredRole="admin" user={user} loading={loading}>
            <AdminDashboard user={user} setUser={setUser} />
          </ProtectedRoute>
        } />
        <Route path="/admin/leagues" element={
          <ProtectedRoute requiredRole="admin" user={user} loading={loading}>
            <LeagueManager user={user} setUser={setUser} />
          </ProtectedRoute>
        } />
        <Route path="/admin/teams" element={
          <ProtectedRoute requiredRole="admin" user={user} loading={loading}>
            <TeamManager user={user} setUser={setUser} />
          </ProtectedRoute>
        } />
        <Route path="/admin/players" element={
          <ProtectedRoute requiredRole="admin" user={user} loading={loading}>
            <PlayerManager user={user} setUser={setUser} />
          </ProtectedRoute>
        } />
        <Route path="/admin/matches" element={
          <ProtectedRoute requiredRole="admin" user={user} loading={loading}>
            <MatchScheduler user={user} setUser={setUser} />
          </ProtectedRoute>
        } />
        <Route path="/admin/users" element={
          <ProtectedRoute requiredRole="admin" user={user} loading={loading}>
            <UserManager user={user} setUser={setUser} />
          </ProtectedRoute>
        } />
        <Route path="/admin/manage-admins" element={
          <ProtectedRoute requiredRole="admin" user={user} loading={loading}>
            <ManageAdminsPage user={user} setUser={setUser} />
          </ProtectedRoute>
        } />
        <Route path="/admin/manage-users" element={
          <ProtectedRoute requiredRole="admin" user={user} loading={loading}>
            <ManageUsersPage user={user} setUser={setUser} />
          </ProtectedRoute>
        } />
        <Route path="/admin/bulk-data" element={
          <ProtectedRoute requiredRole="admin" user={user} loading={loading}>
            <BulkDataManager user={user} setUser={setUser} />
          </ProtectedRoute>
        } />

        {/* Scorer Routes */}
        <Route path="/scorer" element={
          <ProtectedRoute requiredRole="scorer" user={user} loading={loading}>
            <ScorerDashboard user={user} setUser={setUser} />
          </ProtectedRoute>
        } />
        <Route path="/scorer/match/:matchId/scoring" element={
          <ProtectedRoute requiredRole="scorer" user={user} loading={loading}>
            <LiveScoring user={user} setUser={setUser} />
          </ProtectedRoute>
        } />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      {showBottomNav && <BottomNav user={user} />}
    </>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <AppRouter user={user} setUser={setUser} loading={loading} />
      </BrowserRouter>
    </div>
  );
}

export default App;
